import { Avatar, Badge, Tooltip } from '@mantine/core';
import { IconMicrophone, IconPhoto } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { MEMORY_KIND_COLOR, memoryKindLabel } from 'src/Components/Memories/memoryKinds';
import { formatMemoryDate } from 'src/Utils/memoryDate';

import styles from './MemoryCard.module.scss';
import { Props } from './MemoryCard.types';

// Who was there: a few first names, the rest as « +3 », so the card stays readable.
const PEOPLE_SHOWN = 4;

const MemoryCard = ({ memory, onClick }: Props) => {
  const { t } = useTranslation();
  const dateLabel = formatMemoryDate(
    t,
    memory.takenAt,
    memory.takenAtPrecision,
    memory.takenAtApproximate
  );
  const context = [
    memory.rooms.map(room => room.name).join(', '),
    memory.photoCount > 0
      ? t('memories.card.photos', { count: memory.photoCount, defaultValue: '{{count}} photos' })
      : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const coverUrl = memory.cover?.previewMediumUrl ?? memory.cover?.previewThumbnailUrl;
  const people = memory.people.slice(0, PEOPLE_SHOWN);
  const morePeople = memory.people.length - PEOPLE_SHOWN;

  return (
    <button type="button" className={styles.container} onClick={onClick}>
      <div className={styles.cover}>
        {coverUrl ? (
          <img src={coverUrl} alt="" loading="lazy" />
        ) : (
          <IconPhoto size={24} className={styles.coverPlaceholder} />
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.title}>{memory.title}</div>
        {(!!dateLabel || !!memory.memoryKind) && (
          <div className={styles.topLine}>
            {dateLabel && <span className={styles.date}>{dateLabel}</span>}
            {memory.memoryKind && (
              <Badge size="sm" variant="light" color={MEMORY_KIND_COLOR[memory.memoryKind]}>
                {memoryKindLabel(t, memory.memoryKind)}
              </Badge>
            )}
          </div>
        )}
        {context && <div className={styles.context}>{context}</div>}
        {people.length > 0 && (
          <div className={styles.context}>
            {people.map(person => person.displayName).join(', ')}
            {morePeople > 0
              ? ` ${t('memories.card.morePeople', '+{{count}}', { count: morePeople })}`
              : ''}
          </div>
        )}
        {memory.recordingCount > 0 && (
          <div className={styles.voiceLine}>
            <IconMicrophone size={16} />
            <span>
              {t('memories.card.recordings', {
                count: memory.recordingCount,
                defaultValue: '{{count}} récits à écouter',
              })}
            </span>
            <Avatar.Group spacing="xs">
              {memory.storytellers.map(storyteller => (
                <Tooltip key={storyteller.displayName} label={storyteller.displayName} withArrow>
                  <Avatar size="sm" color="initials" name={storyteller.displayName}>
                    {storyteller.initials}
                  </Avatar>
                </Tooltip>
              ))}
            </Avatar.Group>
          </div>
        )}
      </div>
    </button>
  );
};

export default MemoryCard;
